// v2/router/v2-routes.tsx — Full v2 route tree with lazy-loaded feature pages

import React, { Suspense, lazy } from 'react';
import { RouteObject } from 'react-router-dom';
import { LayoutShell } from '../layout/LayoutShell';
import { ProtectedRoute } from './ProtectedRoute';
import { ErrorBoundary } from '../shared/components/ErrorBoundary';
import { Action } from '../shared/types/domain';

// Import design tokens CSS so custom properties are available across all v2 pages
import '../design-system/tokens.css';

// ─── Lazy-loaded feature pages ──────────────────────────────────────────────
const LazyDashboard = lazy(() =>
  import('../features/dashboard/DashboardPage').then((m) => ({ default: m.DashboardPage }))
);

// Leads
const LazyLeadListPage = lazy(() =>
  import('../features/leads').then((m) => ({ default: m.LeadListPage }))
);
const LazyCreateLeadPage = lazy(() =>
  import('../features/leads').then((m) => ({ default: m.CreateLeadPage }))
);
const LazyLeadDetailsPage = lazy(() =>
  import('../features/leads').then((m) => ({ default: m.LeadDetailsPage }))
);

// Applications
const LazyApplicationListPage = lazy(() =>
  import('../features/applications').then((m) => ({ default: m.ApplicationListPage }))
);
const LazyCreateApplicationPage = lazy(() =>
  import('../features/applications').then((m) => ({ default: m.CreateApplicationPage }))
);
const LazyApplicationDetailsPage = lazy(() =>
  import('../features/applications').then((m) => ({ default: m.ApplicationDetailsPage }))
);
const LazyUploadDocumentPage = lazy(() =>
  import('../features/applications').then((m) => ({ default: m.UploadDocumentPage }))
);

// Tasks
const LazyTaskListPage = lazy(() =>
  import('../features/tasks').then((m) => ({ default: m.TaskListPage }))
);
const LazyCreateTaskPage = lazy(() =>
  import('../features/tasks').then((m) => ({ default: m.CreateTaskPage }))
);
const LazyTaskDetailsPage = lazy(() =>
  import('../features/tasks').then((m) => ({ default: m.TaskDetailsPage }))
);

// Groups
const LazyGroupListPage = lazy(() =>
  import('../features/groups').then((m) => ({ default: m.GroupListPage }))
);
const LazyCreateGroupPage = lazy(() =>
  import('../features/groups').then((m) => ({ default: m.CreateGroupPage }))
);
const LazyGroupDetailsPage = lazy(() =>
  import('../features/groups').then((m) => ({ default: m.GroupDetailsPage }))
);
const LazyAddMemberPage = lazy(() =>
  import('../features/groups').then((m) => ({ default: m.AddMemberPage }))
);

// Residence Verification
const LazyResidenceVerificationPage = lazy(() =>
  import('../features/residence-verification').then((m) => ({ default: m.ResidenceVerificationPage }))
);
const LazyResidenceVerificationViewPage = lazy(() =>
  import('../features/residence-verification').then((m) => ({ default: m.ResidenceVerificationViewPage }))
);

// Login History
const LazyUserLoginHistoryPage = lazy(() =>
  import('../features/login-history').then((m) => ({ default: m.UserLoginHistoryPage }))
);

// Settings
const LazySettingsPage = lazy(() =>
  import('../features/settings/SettingsPage').then((m) => ({ default: m.SettingsPage }))
);

// EMI Calculator
const LazyEmiCalculatorPage = lazy(() =>
  import('../features/emi-calculator').then((m) => ({ default: m.EmiCalculatorPage }))
);

function SuspenseFallback(): React.ReactElement {
  return (
    <div className="flex items-center justify-center min-h-[40vh]">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
    </div>
  );
}

function withSuspense(Component: React.LazyExoticComponent<React.ComponentType>): React.ReactElement {
  return (
    <Suspense fallback={<SuspenseFallback />}>
      <Component />
    </Suspense>
  );
}

/**
 * v2 route object for use with useRoutes().
 * All authenticated routes are wrapped in ProtectedRoute + LayoutShell.
 * v1 routes remain unchanged — v2 routes live under /v2/*.
 */
export const v2RouteObject: RouteObject = {
  path: '/v2',
  element: (
    <ErrorBoundary>
      <ProtectedRoute />
    </ErrorBoundary>
  ),
  children: [
    {
      element: <LayoutShell />,
      children: [
        { index: true, element: withSuspense(LazyDashboard) },

        // Leads
        { path: 'leads', element: withSuspense(LazyLeadListPage) },
        { path: 'leads/create', element: withSuspense(LazyCreateLeadPage) },
        { path: 'leads/:id', element: withSuspense(LazyLeadDetailsPage) },

        // Applications
        { path: 'applications', element: withSuspense(LazyApplicationListPage) },
        { path: 'applications/new', element: withSuspense(LazyCreateApplicationPage) },
        { path: 'applications/:id', element: withSuspense(LazyApplicationDetailsPage) },
        { path: 'applications/:id/documents', element: withSuspense(LazyUploadDocumentPage) },

        // Tasks
        { path: 'tasks', element: withSuspense(LazyTaskListPage) },
        { path: 'tasks/create', element: withSuspense(LazyCreateTaskPage) },
        { path: 'tasks/:id', element: withSuspense(LazyTaskDetailsPage) },

        // Groups
        { path: 'groups', element: withSuspense(LazyGroupListPage) },
        { path: 'groups/create', element: withSuspense(LazyCreateGroupPage) },
        { path: 'groups/:id', element: withSuspense(LazyGroupDetailsPage) },
        { path: 'groups/:id/add-member', element: withSuspense(LazyAddMemberPage) },

        // Residence Verification
        { path: 'residence-verification/:applicationId', element: withSuspense(LazyResidenceVerificationPage) },
        { path: 'residence-verification/:applicationId/view', element: withSuspense(LazyResidenceVerificationViewPage) },

        // Login History (permission-gated)
        {
          element: <ProtectedRoute requiredAction={Action.VIEW_USER_LOGIN_HISTORY} />,
          children: [
            { path: 'login-history', element: withSuspense(LazyUserLoginHistoryPage) },
          ],
        },

        // EMI Calculator
        { path: 'emi-calculator', element: withSuspense(LazyEmiCalculatorPage) },

        // Settings
        { path: 'settings', element: withSuspense(LazySettingsPage) },
      ],
    },
  ],
};
